import React, { Fragment, useRef } from 'react';
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { roomTypes } from './MessageInput';

const AddButton = React.forwardRef<
    HTMLTextAreaElement,
    {
        roomType: string;
        handleAddAttachment: (file: File) => void;
        disablePremadeAnswers?: boolean;
        chatSessionKey: ChatSessionKey;
        /** Dark variant, see MessagesPage. */
        videoDark?: boolean;
    }
>(
    (
        {
            roomType,
            handleAddAttachment,
            disablePremadeAnswers = false,
            chatSessionKey,
            videoDark = false,
        },
        inputRef,
    ) => {
        const { sendNotification, i18next } = useAppContext();
        const { openModal } = useModal();
        const fileInputRef = useRef<HTMLInputElement>(null);

        const showPostOption = [roomTypes.room, roomTypes.public].includes(
            roomType,
        );

        return (
            <>
                <Menu as="div">
                    {({ open, close }) => {
                        return (
                            <div className="relative">
                                <Transition
                                    show={open}
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div
                                        className={twMerge(
                                            'shadow-base absolute start-0 -top-2 z-30 flex w-[320px] -translate-y-full transform flex-col gap-4 rounded-sm p-3',
                                            videoDark
                                                ? 'rounded-xl bg-gray-700 text-white'
                                                : 'bg-white text-gray-800',
                                        )}
                                    >
                                        <MenuItems
                                            static={true}
                                            as="div"
                                            className="flex max-h-[520px] origin-bottom-right flex-col gap-2 overflow-hidden outline-hidden rtl:origin-top-left"
                                        >
                                            {
                                                <>
                                                    <MenuItem>
                                                        <button
                                                            className={twMerge(
                                                                'group flex h-12 w-full items-center gap-x-2 rounded-sm p-2',
                                                                videoDark
                                                                    ? 'hover:bg-gray-600'
                                                                    : 'hover:bg-gray-200',
                                                            )}
                                                            type="button"
                                                            onClick={() => {
                                                                fileInputRef.current.click();
                                                                close();
                                                            }}
                                                        >
                                                            <Icon
                                                                iconKey="attachment"
                                                                className={
                                                                    videoDark
                                                                        ? 'text-gray-300 group-hover:text-white'
                                                                        : 'text-gray-600 group-hover:text-gray-700'
                                                                }
                                                            />
                                                            <p>
                                                                {i18next.t(
                                                                    'Chat.Tooltip.Attachment',
                                                                )}
                                                            </p>
                                                        </button>
                                                    </MenuItem>

                                                    {!disablePremadeAnswers && (
                                                        <MenuItem>
                                                            <div
                                                                id="button-premade-answers"
                                                                className={twMerge(
                                                                    'group flex h-12 w-full cursor-pointer items-center gap-x-2 rounded-sm p-2',
                                                                    videoDark
                                                                        ? 'hover:bg-gray-600'
                                                                        : 'hover:bg-gray-200',
                                                                )}
                                                                onClick={() =>
                                                                    sendNotification(
                                                                        {
                                                                            type: 'open-premade-answers',
                                                                            edit: false,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    iconKey="quick_response"
                                                                    className={
                                                                        videoDark
                                                                            ? 'text-gray-300 group-hover:text-white'
                                                                            : 'text-gray-600 group-hover:text-gray-700'
                                                                    }
                                                                />
                                                                <p className="flex-1 text-start">
                                                                    {i18next.t(
                                                                        'premadeAnswer.dialogTitle',
                                                                    )}
                                                                </p>
                                                                <IconButton
                                                                    aria-label={i18next.t(
                                                                        'Tooltip.Nav.Title.Edit',
                                                                    )}
                                                                    onClick={(
                                                                        event,
                                                                    ) => {
                                                                        event.stopPropagation();

                                                                        sendNotification(
                                                                            {
                                                                                type: 'open-premade-answers',
                                                                                edit: true,
                                                                            },
                                                                        );
                                                                    }}
                                                                    className="sr-only-until-hover-or-focus block"
                                                                    type="button"
                                                                >
                                                                    <Icon iconKey="general" />
                                                                </IconButton>
                                                            </div>
                                                        </MenuItem>
                                                    )}

                                                    {showPostOption && (
                                                        <MenuItem>
                                                            <button
                                                                className={twMerge(
                                                                    'group flex h-12 w-full items-center gap-x-2 rounded-sm p-2',
                                                                    videoDark
                                                                        ? 'hover:bg-gray-600'
                                                                        : 'hover:bg-gray-200',
                                                                )}
                                                                type="button"
                                                                onClick={() =>
                                                                    openModal(
                                                                        'createPost',
                                                                        {
                                                                            id: chatSessionKey,
                                                                        },
                                                                    )
                                                                }
                                                            >
                                                                <Icon
                                                                    iconKey="message_post"
                                                                    className={
                                                                        videoDark
                                                                            ? 'text-gray-300 group-hover:text-white'
                                                                            : 'text-gray-600 group-hover:text-gray-700'
                                                                    }
                                                                />
                                                                <p>
                                                                    {i18next.t(
                                                                        'chat.post.create',
                                                                    )}
                                                                </p>
                                                            </button>
                                                        </MenuItem>
                                                    )}
                                                </>
                                            }
                                        </MenuItems>
                                    </div>
                                </Transition>

                                <MenuButton
                                    id="messages-add-button"
                                    className={twMerge(
                                        'size-8 items-center rounded-sm',
                                        videoDark
                                            ? 'bg-gray-500 text-gray-200 hover:bg-gray-400 hover:text-white'
                                            : 'hover:darken-10 bg-gray-300 text-gray-600 hover:text-gray-700',
                                    )}
                                >
                                    <span className="sr-only">
                                        {i18next.t('chatsettings.add')}
                                    </span>

                                    <Icon iconKey="add" />
                                </MenuButton>
                            </div>
                        );
                    }}
                </Menu>
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                        handleAddAttachment(e.target.files[0]);
                        e.target.value = null;
                    }}
                />
            </>
        );
    },
);

export default AddButton;
